import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useState } from 'react';
import {
  useGetSupplierQuery,
  useGetSupplierHistoryQuery,
  useCreateSupplierPaymentMutation,
} from '../../../store/api/purchaseApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function SupplierDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    paymentDate: new Date().toISOString().split('T')[0],
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  const { data: supplier, isLoading: loadingSupplier, error: supplierError } = useGetSupplierQuery(id);
  const { data: history, isLoading: loadingHistory, error: historyError } = useGetSupplierHistoryQuery(id);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';
  const [createPayment] = useCreateSupplierPaymentMutation();

  const formatAmount = (value) => {
    const amount = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (e) {
      return `${currency} ${amount.toFixed(2)}`;
    }
  };

  if (loadingSupplier || loadingHistory) {
    return <Typography>Loading...</Typography>;
  }
  if (supplierError) {
    return <Typography color="error">Error loading supplier</Typography>;
  }
  if (historyError) {
    return <Typography color="error">Error loading purchase history</Typography>;
  }

  const purchaseOrders = history?.purchaseOrders || [];
  const purchaseReturns = history?.purchaseReturns || [];
  const openBalance = purchaseOrders.reduce(
    (sum, po) => sum + (po.purchaseType === 'credit' ? Number(po.balanceAmount || 0) : 0),
    0
  );

  const handleSubmitPayment = async () => {
    const amount = Number(paymentForm.amount || 0);
    if (!amount || amount <= 0) {
      alert('Amount must be greater than zero');
      return;
    }
    try {
      await createPayment({
        supplierId: id,
        ...paymentForm,
      }).unwrap();
      setPaymentDialogOpen(false);
      setPaymentForm((prev) => ({
        ...prev,
        amount: '',
        referenceNo: '',
        notes: '',
      }));
      window.location.reload();
    } catch (error) {
      alert(error?.data?.message || 'Error recording payment');
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          mb: 4,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/purchases/suppliers')} size="small">
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              {supplier?.name || 'Supplier'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {supplier?.code && `Code: ${supplier.code}  · `}{supplier?.email || supplier?.phone || ''}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {`Outstanding Credit Balance: ${formatAmount(openBalance)}`}
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" onClick={() => setPaymentDialogOpen(true)} sx={{ borderRadius: 2 }}>
          Record Payment
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Purchase Orders" />
        <Tab label="Purchase Returns" />
      </Tabs>

      {tab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Purchase Orders
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Invoice Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Balance (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseOrders.length > 0 ? (
                    purchaseOrders.map((po) => (
                      <TableRow key={po.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{po.orderNumber}</TableCell>
                        <TableCell>{po.invoiceNumber || '-'}</TableCell>
                        <TableCell>
                          {po.invoiceDate ? new Date(po.invoiceDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(po.totalAmount)}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {formatAmount(po.balanceAmount)}
                        </TableCell>
                        <TableCell>{po.status}</TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/purchases/orders/${po.id}`)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No purchase orders for this supplier.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Purchase Returns
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Return Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Return Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{`Total (${currency})`}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {purchaseReturns.length > 0 ? (
                    purchaseReturns.map((pr) => (
                      <TableRow key={pr.id} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{pr.returnNumber}</TableCell>
                        <TableCell>
                          {pr.returnDate ? new Date(pr.returnDate).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{formatAmount(pr.totalAmount)}</TableCell>
                        <TableCell>{pr.status || '-'}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">No purchase returns for this supplier.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Record Supplier Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="date"
              label="Payment Date"
              value={paymentForm.paymentDate}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="number"
              label={`Amount (${currency})`}
              value={paymentForm.amount}
              onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={openBalance > 0 ? `Outstanding: ${formatAmount(openBalance)}` : ''}
            />
            <TextField
              fullWidth
              select
              label="Payment Method"
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Reference No"
              value={paymentForm.referenceNo}
              onChange={(e) => setPaymentForm({ ...paymentForm, referenceNo: e.target.value })}
            />
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={paymentForm.notes}
              onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitPayment}>
            Save Payment
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SupplierDetails;

